/**
 * PropertyAskQuestionPage
 *
 * Page for investors to ask a question about a property.
 * Shows property summary on the left and question form on the right.
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Redact, trackInformationRequestSent } from '@/common/analytics'
import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { Icon } from '@/common/components/icon'
import { Button } from '@/common/components/button'
import { Textarea } from '@/common/components/textarea'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { getProperty, requestPropertyInfo, type PropertyStatus } from '@/features/properties/api/properties'
import { DEMO_PROPERTY_ID } from '@/features/product-tour'
import { STATUS_CONFIG, canInteractWithProperty } from '@/features/properties/components/PropertyStatusBadge'
import * as styles from './ask.css'

export const Route = createFileRoute({
  component: PropertyAskQuestionPage,
})

// =============================================================================
// Schema
// =============================================================================

const askQuestionSchema = z.object({
  message: z
    .string()
    .min(10, 'Question must be at least 10 characters')
    .max(1000, 'Question must be 1000 characters or less'),
})

type AskQuestionFormData = z.infer<typeof askQuestionSchema>

// =============================================================================
// Helpers
// =============================================================================

function formatSpecs(beds?: number | null, baths?: number | null, sqft?: number | null): string {
  const parts: string[] = []
  if (beds != null) parts.push(`${beds} beds`)
  if (baths != null) parts.push(`${baths} baths`)
  if (sqft != null) parts.push(`${sqft.toLocaleString()} sqft`)
  return parts.join('  ·  ')
}

// =============================================================================
// Component
// =============================================================================

function PropertyAskQuestionPage() {
  const { id: propertyId } = useParams({ strict: false })
  const navigate = useNavigate()
  const { token, roles } = useAuth()
  const { showSuccessToast, showErrorToast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isInvestor = roles?.includes('investor')

  // Fetch property data
  const {
    data: property,
    isLoading: propertyLoading,
    error: propertyError,
  } = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => getProperty(token!, propertyId!),
    enabled: !!token && !!propertyId,
  })

  // Redirect if property is sold/removed
  useEffect(() => {
    if (property && !canInteractWithProperty(property.status as PropertyStatus)) {
      navigate({ to: '/properties/$id', params: { id: property.id } })
    }
  }, [property, navigate])

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AskQuestionFormData>({
    resolver: zodResolver(askQuestionSchema) as never,
    defaultValues: {
      message: '',
    },
  })

  const onSubmit = async (data: AskQuestionFormData) => {
    if (!token || !propertyId) {
      showErrorToast('Please log in to ask a question')
      return
    }

    // Demo property from the product tour — skip the API call
    if (propertyId === DEMO_PROPERTY_ID) {
      showSuccessToast('Question submitted successfully!')
      navigate({ to: '/properties/$id', params: { id: propertyId } })
      return
    }

    setIsSubmitting(true)

    try {
      const request = await requestPropertyInfo(token, propertyId, data.message)

      // Fire the Information Request Sent client event. Only the
      // opaque request/property ids — never the message body.
      trackInformationRequestSent({ request_id: request.id, property_id: propertyId })

      showSuccessToast('Question submitted successfully!')
      navigate({ to: '/properties/$id', params: { id: propertyId } })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit question'
      showErrorToast(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Non-investors shouldn't access this page
  if (!isInvestor) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Text variant="bodyLg" weight="bold" color="error">
            Access Denied
          </Text>
          <Text color="muted">Only investors can ask questions.</Text>
          <Link to="/properties">
            <Button variant="primary">Back to Properties</Button>
          </Link>
        </div>
      </div>
    )
  }

  // Loading state
  if (propertyLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state
  if (propertyError || !property) {
    return (
      <div className={styles.container}>
        <Link to="/properties" className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back
        </Link>
        <div className={styles.errorContainer}>
          <Text as="h2" variant="h4" className={styles.errorTitle}>Property Not Found</Text>
          <Text color="muted">
            The property you're looking for doesn't exist or you don't have permission to view it.
          </Text>
        </div>
      </div>
    )
  }

  // Get first image for property summary
  const firstImage = property.media?.find((m) => m.media_type === 'photo')

  return (
    <div className={styles.container}>
      {/* Back Link */}
      <Link to="/properties/$id" params={{ id: property.id }} className={styles.backLink}>
        <Icon name="arrow_back" size={16} />
        Back
      </Link>

      {/* Two-column layout */}
      <div className={styles.content}>
        {/* Property Summary (left) */}
        <div className={styles.propertySummary}>
          {firstImage ? (
            <img
              src={firstImage.file_url}
              alt={property.address.street_address}
              className={styles.propertyImage}
            />
          ) : (
            <div className={styles.propertyImagePlaceholder}>
              <Icon name="home" size={48} className={styles.placeholderIcon} />
            </div>
          )}

          <div className={styles.propertyDetails}>
            <div className={styles.statusBadge} data-color={STATUS_CONFIG[property.status as PropertyStatus].color}>
              <span className={styles.statusDot} data-color={STATUS_CONFIG[property.status as PropertyStatus].color} />
              {STATUS_CONFIG[property.status as PropertyStatus].label}
            </div>

            {/* The property address is PII — redact the display
                block (parity with the offer.tsx address block). */}
            <Redact as="span" className={styles.propertyAddress}>
              <Text as="span" variant="body" weight="bold">
                {property.address.street_address}
                {property.address.unit_number && ` ${property.address.unit_number}`}
              </Text>
            </Redact>
            <Redact as="span" className={styles.propertyCityStateZip}>
              <Text as="span" variant="body">
                {property.address.city}, {property.address.state} {property.address.zipcode}
              </Text>
            </Redact>
            <Text as="span" variant="body" color="muted" className={styles.propertySpecs}>
              {formatSpecs(property.beds, property.baths, property.sqft)}
            </Text>
          </div>
        </div>

        {/* Question Form (right) */}
        <div className={styles.formCard}>
          <Text variant="body">Ask a question</Text>
          <Text variant="bodySm" color="muted" className={styles.formDescription}>
            Have a question about this property? Send a message to the listing agent.
          </Text>

          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            {/* Question */}
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label="Your question"
                  placeholder="E.g., What is the condition of the roof? Are there any known issues with the foundation?"
                  rows={6}
                  maxLength={1000}
                  error={!!errors.message}
                  disabled={isSubmitting}
                />
              )}
            />
            {errors.message && (
              <span className={styles.errorMessage}>{errors.message.message}</span>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? 'Sending...' : 'Send question'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  )
}
